import { useCallback, type Dispatch, type SetStateAction } from "react";
import { NavLink } from "react-router-dom";
import type { AuthContextValue } from "../context/AuthContext";
import {
  IconAgent,
  IconBriefcase,
  IconBuilding,
  IconChartBar,
  IconClipboard,
  IconFolder,
  IconHome,
  IconInward,
  IconLayers,
  IconMilestone,
  IconReceipt,
  IconResources,
  IconTimeline,
  IconUserPlus,
} from "./Icons";
import { SidebarSectionFlyout } from "./SidebarSectionFlyout";
import { canAccessApplicationsHub } from "../config/applicationHubPermissions";
import { canAnyPlanning } from "../config/consolePermissionKeys";

function navClass({ isActive }: { isActive: boolean }) {
  return "nav-link" + (isActive ? " nav-link--active" : "");
}

export type ShellNavLinksProps = {
  idPrefix: string;
  can: AuthContextValue["can"];
  authRequired: boolean;
  user: AuthContextValue["user"];
  applicationsHubOpen: boolean;
  setApplicationsHubOpen: Dispatch<SetStateAction<boolean>>;
  mastersOpen: boolean;
  setMastersOpen: Dispatch<SetStateAction<boolean>>;
  procurementOpen: boolean;
  setProcurementOpen: Dispatch<SetStateAction<boolean>>;
  inventoryOpen: boolean;
  setInventoryOpen: Dispatch<SetStateAction<boolean>>;
  projectsOpen: boolean;
  setProjectsOpen: Dispatch<SetStateAction<boolean>>;
  invoiceOpen: boolean;
  setInvoiceOpen: Dispatch<SetStateAction<boolean>>;
  executionOpen: boolean;
  setExecutionOpen: Dispatch<SetStateAction<boolean>>;
  administrationOpen: boolean;
  setAdministrationOpen: Dispatch<SetStateAction<boolean>>;
};

export function ShellNavLinks({
  idPrefix,
  can,
  authRequired,
  user,
  applicationsHubOpen,
  setApplicationsHubOpen,
  mastersOpen,
  setMastersOpen,
  procurementOpen,
  setProcurementOpen,
  inventoryOpen,
  setInventoryOpen,
  projectsOpen,
  setProjectsOpen,
  invoiceOpen,
  setInvoiceOpen,
  executionOpen,
  setExecutionOpen,
  administrationOpen,
  setAdministrationOpen,
}: ShellNavLinksProps) {
  const showLeadsHub = can("leads") || can("leads_import") || canAccessApplicationsHub(can);
  const showProjects = can("applications") || canAccessApplicationsHub(can);
  const showMasters =
    can("subcontractor_masters") ||
    can("engineer_masters") ||
    can("agents") ||
    can("geo_directory") ||
    can("geo_directory_admin") ||
    canAnyPlanning(can) ||
    can("geo_zones") ||
    can("inventory_stores_yards");
  const showProcurement = can("inventory_issue_po") || can("inventory_issue_material");
  const showInvoice = can("bills_inward");
  const showInventory =
    can("inventory_inward") ||
    can("inventory_store_stocks") ||
    (can("inventory_outward") && can("inventory_issue_material"));
  const canMilestonesHub = can("applications") && can("milestone_templates");
  const showExecution =
    canMilestonesHub || can("bills_outward") || can("inventory_goods_receipts");
  const showReports = can("reports");
  const showAdministration = Boolean(
    authRequired && user && (user.role === "superadmin" || can("console_users") || can("audit_log")),
  );

  const mastersHeadingId = `${idPrefix}-nav-masters-heading`;
  const mastersNavId = `${idPrefix}-sidebar-masters-nav`;
  const procurementHeadingId = `${idPrefix}-nav-procurement-heading`;
  const procurementNavId = `${idPrefix}-sidebar-procurement-nav`;
  const inventoryHeadingId = `${idPrefix}-nav-inventory-heading`;
  const inventoryNavId = `${idPrefix}-sidebar-inventory-nav`;
  const leadsHeadingId = `${idPrefix}-nav-leads-heading`;
  const leadsNavId = `${idPrefix}-sidebar-leads-nav`;
  const projectsHeadingId = `${idPrefix}-nav-projects-heading`;
  const projectsNavId = `${idPrefix}-sidebar-projects-nav`;
  const invoiceHeadingId = `${idPrefix}-nav-invoice-heading`;
  const invoiceNavId = `${idPrefix}-sidebar-invoice-nav`;
  const executionHeadingId = `${idPrefix}-nav-execution-heading`;
  const executionNavId = `${idPrefix}-sidebar-execution-nav`;
  const administrationHeadingId = `${idPrefix}-nav-administration-heading`;
  const administrationNavId = `${idPrefix}-sidebar-administration-nav`;

  const closeAllSections = useCallback(() => {
    setApplicationsHubOpen(false);
    setProjectsOpen(false);
    setProcurementOpen(false);
    setInventoryOpen(false);
    setMastersOpen(false);
    setInvoiceOpen(false);
    setExecutionOpen(false);
    setAdministrationOpen(false);
  }, [
    setApplicationsHubOpen,
    setProjectsOpen,
    setProcurementOpen,
    setInventoryOpen,
    setMastersOpen,
    setInvoiceOpen,
    setExecutionOpen,
    setAdministrationOpen,
  ]);

  const sectionToggle = (open: boolean, setter: Dispatch<SetStateAction<boolean>>) => () => {
    if (open) {
      setter(false);
      return;
    }
    closeAllSections();
    setter(true);
  };

  return (
    <div className="sidebar__nav">
      <div className="sidebar__section-label">Menu</div>
      <NavLink to="/" end className={navClass}>
        <IconHome className="nav-link__icon" />
        <span className="nav-link__text">Home</span>
      </NavLink>

      {showReports ? (
        <NavLink to="/reports" className={navClass} title="Comprehensive data reports and downloads">
          <IconChartBar className="nav-link__icon" />
          <span className="nav-link__text">Reports Hub</span>
        </NavLink>
      ) : null}

      {showLeadsHub ? (
        <SidebarSectionFlyout
          id={leadsHeadingId}
          navId={leadsNavId}
          label="Leads"
          icon={<IconLayers className="sidebar__section-toggle__icon" />}
          open={applicationsHubOpen}
          onToggle={sectionToggle(applicationsHubOpen, setApplicationsHubOpen)}
        >
          {can("leads") || can("leads_import") ? (
            <NavLink
              to={can("leads") ? "/leads" : "/leads?import=1"}
              className={navClass}
              title={can("leads") ? "Lead list and PDF/Excel import" : "Import leads (PDF or Excel)"}
            >
              <IconLayers className="nav-link__icon" />
              <span className="nav-link__text">{can("leads") ? "Leads" : "Leads import"}</span>
            </NavLink>
          ) : null}
          {canAccessApplicationsHub(can) ? (
            <NavLink to="/applications" className={navClass} title="Browse beneficiary applicants">
              <IconFolder className="nav-link__icon" />
              <span className="nav-link__text">Applicants</span>
            </NavLink>
          ) : null}
        </SidebarSectionFlyout>
      ) : null}

      {showProjects ? (
        <SidebarSectionFlyout
          id={projectsHeadingId}
          navId={projectsNavId}
          label="Projects"
          icon={<IconBriefcase className="sidebar__section-toggle__icon" />}
          open={projectsOpen}
          onToggle={sectionToggle(projectsOpen, setProjectsOpen)}
        >
          {showProjects ? (
            <NavLink to="/subcontractor-to-beneficiary" className={navClass} title="Subcontractor to beneficiaries">
              <IconBriefcase className="nav-link__icon" />
              <span className="nav-link__text">Beneficiaries</span>
            </NavLink>
          ) : null}
          {showProjects ? (
            <NavLink to="/timeline-approvals" className={navClass} title="Review subcontractor timeline photos">
              <IconTimeline className="nav-link__icon" />
              <span className="nav-link__text">Timeline approvals</span>
            </NavLink>
          ) : null}
          {showProjects ? (
            <NavLink to="/project-initiation" className={navClass} title="Set planned start dates for beneficiary groups">
              <IconTimeline className="nav-link__icon" />
              <span className="nav-link__text">Project initiation</span>
            </NavLink>
          ) : null}
        </SidebarSectionFlyout>
      ) : null}

      {showProcurement ? (
        <SidebarSectionFlyout
          id={procurementHeadingId}
          navId={procurementNavId}
          label="Procurement"
          icon={<IconReceipt className="sidebar__section-toggle__icon" />}
          open={procurementOpen}
          onToggle={sectionToggle(procurementOpen, setProcurementOpen)}
        >
          {can("inventory_issue_po") ? (
            <NavLink to="/inventory/po-format" className={navClass} title="Saved PO letterhead and GST defaults">
              <IconReceipt className="nav-link__icon" />
              <span className="nav-link__text">PO format</span>
            </NavLink>
          ) : null}
          {can("inventory_issue_po") ? (
            <NavLink to="/inventory/po-register" className={navClass} title="Generate and monitor issue PO">
              <IconLayers className="nav-link__icon" />
              <span className="nav-link__text">PO register</span>
            </NavLink>
          ) : null}
          {can("inventory_issue_material") ? (
            <NavLink to="/inventory/issue-material" className={navClass} title="Issue material register">
              <IconLayers className="nav-link__icon" />
              <span className="nav-link__text">Material register</span>
            </NavLink>
          ) : null}
        </SidebarSectionFlyout>
      ) : null}

      {showInventory ? (
        <SidebarSectionFlyout
          id={inventoryHeadingId}
          navId={inventoryNavId}
          label="Inventory"
          icon={<IconInward className="sidebar__section-toggle__icon" />}
          open={inventoryOpen}
          onToggle={sectionToggle(inventoryOpen, setInventoryOpen)}
        >
          {can("inventory_inward") ? (
            <NavLink to="/inventory/inward" className={navClass} title="Inventory inward">
              <IconInward className="nav-link__icon" />
              <span className="nav-link__text">Inventory inward</span>
            </NavLink>
          ) : null}
          {can("inventory_outward") && can("inventory_issue_material") ? (
            <NavLink to="/inventory/outward" className={navClass} title="Inventory outward">
              <IconBriefcase className="nav-link__icon" />
              <span className="nav-link__text">Inventory outward</span>
            </NavLink>
          ) : null}
          {can("inventory_store_stocks") ? (
            <NavLink to="/inventory/store-stocks" className={navClass} title="Store stocks">
              <IconFolder className="nav-link__icon" />
              <span className="nav-link__text">Store stocks</span>
            </NavLink>
          ) : null}
          {can("inventory_store_stocks") ? (
            <NavLink to="/inventory/store-audit" className={navClass} title="Store audit worksheet">
              <IconClipboard className="nav-link__icon" />
              <span className="nav-link__text">Store audit</span>
            </NavLink>
          ) : null}
        </SidebarSectionFlyout>
      ) : null}

      {showMasters ? (
        <SidebarSectionFlyout
          id={mastersHeadingId}
          navId={mastersNavId}
          label="Masters"
          icon={<IconFolder className="sidebar__section-toggle__icon" />}
          open={mastersOpen}
          onToggle={sectionToggle(mastersOpen, setMastersOpen)}
        >
            {can("subcontractor_masters") ? (
              <NavLink
                to="/subcontractor-onboarding"
                className={navClass}
                title="Register subcontracting firms for reuse across beneficiaries"
              >
                <IconUserPlus className="nav-link__icon" />
                <span className="nav-link__text">Subcontractor</span>
              </NavLink>
            ) : null}
            {can("engineer_masters") ? (
              <NavLink
                to="/engineer-onboarding"
                className={navClass}
                title="Register site engineers for assignment to beneficiaries"
              >
                <IconClipboard className="nav-link__icon" />
                <span className="nav-link__text">Engineer</span>
              </NavLink>
            ) : null}
            {can("agents") ? (
              <NavLink to="/agent-master" className={navClass} title="Field agents and assigned villages">
                <IconAgent className="nav-link__icon" />
                <span className="nav-link__text">Agent master</span>
              </NavLink>
            ) : null}
            {can("geo_directory") || can("geo_directory_admin") ? (
              <NavLink
                to="/geo-directory"
                className={navClass}
                title="Maintain districts, mandals, and villages in the geographic directory"
              >
                <IconFolder className="nav-link__icon" />
                <span className="nav-link__text">Geo directory</span>
              </NavLink>
            ) : null}
            {can("milestone_templates") ? (
              <NavLink to="/milestone-master" className={navClass} title="Milestone templates">
                <IconMilestone className="nav-link__icon" />
                <span className="nav-link__text">Milestone master</span>
              </NavLink>
            ) : null}
            {can("construction_timeline") ? (
              <NavLink to="/construction-timeline-master" className={navClass} title="Construction timeline master">
                <IconTimeline className="nav-link__icon" />
                <span className="nav-link__text">Timeline master</span>
              </NavLink>
            ) : null}
            {can("standard_resources") ? (
              <NavLink to="/standard-resources-master" className={navClass} title="Standard resources">
                <IconResources className="nav-link__icon" />
                <span className="nav-link__text">Standard resources</span>
              </NavLink>
            ) : null}
            {can("geo_zones") ? (
              <NavLink to="/zones" className={navClass} title="Geographic zones">
                <IconLayers className="nav-link__icon" />
                <span className="nav-link__text">Zones</span>
              </NavLink>
            ) : null}
            {can("inventory_stores_yards") ? (
              <NavLink to="/inventory/stores-yards" className={navClass} title="Stores and yards">
                <IconBuilding className="nav-link__icon" />
                <span className="nav-link__text">Stores &amp; yards</span>
              </NavLink>
            ) : null}
        </SidebarSectionFlyout>
      ) : null}

      {showInvoice ? (
        <SidebarSectionFlyout
          id={invoiceHeadingId}
          navId={invoiceNavId}
          label="Invoice"
          icon={<IconReceipt className="sidebar__section-toggle__icon" />}
          open={invoiceOpen}
          onToggle={sectionToggle(invoiceOpen, setInvoiceOpen)}
        >
          <NavLink to="/bills/inward/format" className={navClass} title="Invoice letterhead and GST defaults">
            <IconReceipt className="nav-link__icon" />
            <span className="nav-link__text">Invoice format</span>
          </NavLink>
          <NavLink to="/bills/inward" className={navClass} title="Generate and register invoices">
            <IconLayers className="nav-link__icon" />
            <span className="nav-link__text">Invoice register</span>
          </NavLink>
          <NavLink to="/bills/labour-suppliers" className={navClass} title="Labour supplier master for labour invoices">
            <IconUserPlus className="nav-link__icon" />
            <span className="nav-link__text">Labour suppliers</span>
          </NavLink>
        </SidebarSectionFlyout>
      ) : null}

      {showExecution ? (
        <SidebarSectionFlyout
          id={executionHeadingId}
          navId={executionNavId}
          label="Operations"
          icon={<IconMilestone className="sidebar__section-toggle__icon" />}
          open={executionOpen}
          onToggle={sectionToggle(executionOpen, setExecutionOpen)}
        >
          {canMilestonesHub ? (
            <NavLink to="/milestones" className={navClass} title="Milestone progress across dossiers">
              <IconMilestone className="nav-link__icon" />
              <span className="nav-link__text">Milestones</span>
            </NavLink>
          ) : null}
          {can("bills_outward") ? (
            <NavLink to="/bills/outward" className={navClass} title="Bills outward">
              <IconReceipt className="nav-link__icon" />
              <span className="nav-link__text">Bills outward</span>
            </NavLink>
          ) : null}
          {can("inventory_goods_receipts") ? (
            <NavLink to="/inventory/goods-receipts" className={navClass} title="Goods receipts">
              <IconReceipt className="nav-link__icon" />
              <span className="nav-link__text">Goods receipts</span>
            </NavLink>
          ) : null}
        </SidebarSectionFlyout>
      ) : null}

      {showAdministration ? (
        <SidebarSectionFlyout
          id={administrationHeadingId}
          navId={administrationNavId}
          label="Administration"
          icon={<IconUserPlus className="sidebar__section-toggle__icon" />}
          open={administrationOpen}
          onToggle={sectionToggle(administrationOpen, setAdministrationOpen)}
        >
          {can("console_users") || user?.role === "superadmin" ? (
            <NavLink to="/console-users" className={navClass} title="Manage HO console accounts">
              <IconUserPlus className="nav-link__icon" />
              <span className="nav-link__text">Console users</span>
            </NavLink>
          ) : null}
          {can("audit_log") || user?.role === "superadmin" ? (
            <NavLink to="/audit-log" className={navClass} title="View HO console activity audit trail">
              <IconClipboard className="nav-link__icon" />
              <span className="nav-link__text">Audit log</span>
            </NavLink>
          ) : null}
        </SidebarSectionFlyout>
      ) : null}
    </div>
  );
}
